/* ===========================================================================
   Constants mirrored from the Python package.
   =========================================================================== */

export const CARD_W = 488,
  CARD_H = 680; // cardvision/geometry.py
export const CARD_ASPECT = 63.0 / 88.0;
export const ART_BOX = [0.1, 0.13, 0.9, 0.5]; // ART_HASH_BOX

// DetectionSettings defaults.
export const DET = {
  minAreaFraction: 0.01,
  maxAreaFraction: 0.95,
  aspectTolerance: 0.18,
  overlapTolerance: 0.35,
  cannyLow: 50,
  cannyHigh: 150,
  blurKernel: 5,
  approxEpsilon: 0.02,
  backgroundBand: 0.04,
  backgroundDistance: 34.0,
  backgroundClusters: 3,
  backgroundMinShare: 0.05,
  refineMaxShift: 8.0,
  // The temporal change mask (live mode only — a still photo has no "scene as it
  // last settled" to diff against).
  changeThreshold: 14.0,
  changeStaleFraction: 0.6,
};

// RetrievalSettings defaults.
export const RET = {
  shortlist: 20,
  maxDistance: 28,
  weightDhashArt: 0.35,
  weightPhashArt: 0.35,
  weightDhashFull: 0.15,
  weightPhashFull: 0.15,
  weightLegacy: 0.35,
  weightPhash256: 0.3,
  weightColour: 0.15,
  weightTiles: 0.2,
  tileTrim: 2,
  enrichedPool: 512,
  weightEmbed: 1.0,
  embedPool: 512,
  embedScale: 64.0,
};

export const ZERO_SCORE_DISTANCE = 32.0; // retrieve.ZERO_SCORE_DISTANCE

// embed.py: model raster and the ImageNet statistics the backbone was trained under.
export const EMBED_SIZE = 224;
export const IMAGENET_MEAN = [0.485, 0.456, 0.406];
export const IMAGENET_STD = [0.229, 0.224, 0.225];

// Detection runs on a downscaled copy; the warp always samples the full-resolution
// original. A 12 MP phone photo would otherwise spend seconds in the segmentation pass
// for quads that are no more accurate — the card outline is many pixels wide either way.
export const DETECT_MAX_DIM = 1100;

//: Long edge the source image is capped to before anything touches it. The warp reads
//: from this, so it bounds crop quality as well as memory — see decodeImage.
export const INPUT_MAX_DIM = 2400;

//: Live detection raster. Deliberately smaller than DETECT_MAX_DIM: detection cost is
//: quadratic in the long edge, and at 1100 px a pass takes ~500 ms, which is a third of
//: a second of stale outline on every frame. 720 px costs less than half that and loses
//: nothing that matters here — the quad only has to *locate* a card, and recognition
//: re-samples it from the native frame at LIVE_INPUT_MAX_DIM anyway.
export const LIVE_DETECT_MAX_DIM = 720;

//: And the raster live *recognition* re-grabs at. A still photo can afford
//: INPUT_MAX_DIM because it is paid for once; a live frame cannot, and the card fills
//: far more of a held-up camera frame than of a photo of a whole table, so the warp
//: still gets plenty of pixels. Separate constants because the two have separate
//: budgets — sharing one is what previously cost still photos their detection accuracy.
export const LIVE_INPUT_MAX_DIM = 1920;

/**
 * Hold-still gating, mirroring `DetectionSettings` field for field.
 *
 * These were the page's own numbers until they were measured against the package's:
 * one frame at a 28-pixel allowance, against three frames at six. The loose gate fires
 * on the first almost-still frame, which is how a motion-blurred warp reaches
 * retrieval — and a blurred warp does not fail loudly, it returns the wrong card. The
 * drift metric moved too: `stability.quadDrift` is the maximum corner displacement
 * minimised over the four cyclic corner orders, not the mean Euclidean distance in a
 * fixed order.
 */
export const STABILITY = {
  //: DetectionSettings.stability_frames.
  frames: 3,
  //: DetectionSettings.stability_tolerance, in detection pixels.
  tolerance: 6.0,
  //: The drift allowance is the larger of `tolerance` and this fraction of the card's
  //: own size — a card held close fills half the frame, and hand tremor moves its
  //: corners more than a fixed budget essentially forever.
  relativeTolerance: 0.02,
  //: A card tracked continuously this many detector frames without ever holding still
  //: is read anyway. Recognition needs a sharp warp, not a motionless world, and a card
  //: in a hand never manages the latter.
  motionSettleFrames: 12,
  //: Frames kept per settled card, and how many must corroborate the winner before an
  //: ACCEPTED verdict stands — DetectionSettings.consensus_frames / consensus_min_agree.
  //: Glare and motion blur decorrelate between frames, so an accept the other frames of
  //: the window cannot confirm is the single-frame fluke README section 8 worries about.
  consensusFrames: 3,
  consensusMinAgree: 2,
};

//: Background snapshot lifecycle, mirroring ScanSession: refresh after this many
//: card-free frames (table cleared, lighting drifted), or after this many *static*
//: frames with an already-scanned card still in view (a pile just sitting there).
export const LIVE_BACKGROUND_REFRESH_FRAMES = 12;
//: Mean per-pixel difference (subsampled) under which the scene *is* the snapshot —
//: scanner._STATIC_DIFF.
export const LIVE_STATIC_DIFF = 3.0;
//: How long a recognised name + price stays pinned to the card on the canvas after
//: it was last confirmed in place.
export const LIVE_LABEL_MS = 4000;

//: Ceiling on readings returned in "every card" mode. It is a safety rail, not a target:
//: overlap suppression decides how many cards there actually are, and it almost always
//: returns fewer. Fixed rather than exposed, because a lower number does not make the
//: scan better — it just silently drops cards off the end of the list.
export const MAX_CARDS = 20;

//: OpenCV compiled to WebAssembly, fetched only when the precision stage is turned on.
//: One URL for both of its users — the detection kernels and ORB verification — because
//: they share a worker and must not fetch two copies.
export const OPENCV_URL =
  "https://cdn.jsdelivr.net/npm/@techstark/opencv-js@4.10.0-release.1/dist/opencv.js";

export const ASSETS = "assets/";
